import { ConfirmDialog } from '@/shared/components';
import { useTranslation } from 'react-i18next';
import { AssignmentsDialog, type AssignmentSubject } from '@/features/team/AssignmentsDialog';
import type { UserResponse } from '@/shared/api';
import { UserInvitationLinkDialog } from './UserInvitationLinkDialog';
import { UserCreateDialog } from './UserCreateDialog';
import { UserNameDialog } from './UserNameDialog';
import { UserTwoFactorResetDialog } from './UserTwoFactorResetDialog';
import { fullName } from './userStatus';
import type { useUserMutations } from './useUserMutations';

export interface UsersListDialogState {
  create: boolean;
  invitationTarget: UserResponse | null;
  nameTarget: UserResponse | null;
  resetTarget: UserResponse | null;
  deactivateTarget: UserResponse | null;
  assignSubject: AssignmentSubject | null;
}

export const CLOSED_LIST_DIALOGS: UsersListDialogState = {
  create: false,
  invitationTarget: null,
  nameTarget: null,
  resetTarget: null,
  deactivateTarget: null,
  assignSubject: null,
};

interface Props {
  state: UsersListDialogState;
  onChange: (patch: Partial<UsersListDialogState>) => void;
  mutations: ReturnType<typeof useUserMutations>;
}

/** Die Overlays der Liste — reine Verdrahtung, damit die Seite ihre Struktur zeigt. */
export function UsersPageDialogs({ state, onChange, mutations }: Props) {
  const { t } = useTranslation('accountRetention');
  const { deactivate, rename, resetTwoFactor } = mutations;
  const { nameTarget, resetTarget, deactivateTarget } = state;

  return (
    <>
      {state.invitationTarget && (
        <UserInvitationLinkDialog
          user={state.invitationTarget}
          open
          onClose={() => onChange({ invitationTarget: null })}
        />
      )}
      <UserCreateDialog open={state.create} onOpenChange={(open) => onChange({ create: open })} />

      <UserNameDialog
        open={nameTarget !== null}
        onOpenChange={(open) => !open && onChange({ nameTarget: null })}
        email={nameTarget?.email ?? ''}
        firstName={nameTarget?.firstName ?? ''}
        lastName={nameTarget?.lastName ?? ''}
        loading={rename.isPending}
        onSubmit={(name) => nameTarget && rename.mutate({ id: nameTarget.id, ...name })}
      />

      <AssignmentsDialog
        subject={state.assignSubject}
        onOpenChange={(open) => !open && onChange({ assignSubject: null })}
      />

      <UserTwoFactorResetDialog
        open={resetTarget !== null}
        onOpenChange={(open) => !open && onChange({ resetTarget: null })}
        userLabel={resetTarget ? fullName(resetTarget) : ''}
        loading={resetTwoFactor.isPending}
        onSubmit={({ password, code }) =>
          resetTarget && resetTwoFactor.mutate({ id: resetTarget.id, password, code })
        }
      />

      <ConfirmDialog
        open={deactivateTarget !== null}
        onOpenChange={(open) => !open && onChange({ deactivateTarget: null })}
        title={
          deactivateTarget ? `Zugang von ${fullName(deactivateTarget)} sperren?` : 'Zugang sperren?'
        }
        description={t('deactivateDescription')}
        confirmLabel="Zugang sperren"
        loading={deactivate.isPending}
        onConfirm={async () => {
          if (deactivateTarget) await deactivate.mutateAsync(deactivateTarget.id);
        }}
      />
    </>
  );
}
